<template>
  <v-card
    v-if="!hideOnEmpty || isInitialLoading || (!isInitialLoading && hasTasks)"
    elevation="0"
    min-height="4"
    class="ma-0 pa-0 task-relation-list"
  >
    <div
      v-for="(task, index) in parsedTasks"
      :key="index"
      :class="{ 'completed-item': task.isCompleted }"
      class="task-item divider-bottom"
      @click="$emit('selectTask', task.id)"
    >
      <v-hover
        v-slot:default="{ hover }"
        :key="index"
        class="d-flex justify-space-between align-center px-3 py-2 cursor-pointer"
      >
        <div>
          <div class="d-flex align-center" style="max-width: 65%;">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  class="mr-3 cursor-pointer"
                  :color="task.isCompleted ? 'green' : ''"
                  @click.stop="$emit('radioClick', task.status, task.id, index)"
                >
                  {{
                    task.isCompleted ? 'mdi-check-circle' : 'mdi-radiobox-blank'
                  }}
                </v-icon>
              </template>
              {{
                task.isCompleted
                  ? $t('tasks.mark_as_open')
                  : $t('tasks.mark_as_completed')
              }}
            </v-tooltip>
            <div class="text-truncate">
              <p
                class="body3 blue-grey--text text--darken-3 text-truncate mb-0"
              >
                {{ task.name }}
              </p>
              <p
                v-if="task.dueDate"
                class="caption mb-0 text-truncate mt-1"
                :class="task.isOverdue ? 'red--text' : 'blue-grey--text'"
              >
                {{ task.dueDateText }}
                <v-icon v-if="task.isOverdue" color="red" size="16">
                  mdi-alert-circle-outline
                </v-icon>
              </p>
            </div>
          </div>
          <div class="d-flex align-center">
            <PriorityIcon
              v-if="task.priority"
              class="mr-1"
              :priority="task.priority"
            />
            <div class="icon-bubble-avatar">
              <UserSummaryPopover
                v-if="task.assignees.length"
                :account="task.assignees[0]"
              >
                <UserAvatar
                  class="mx-2"
                  size="28px"
                  fontSize="10px"
                  :account="task.assignees[0]"
                  :imageSize="IMAGE_SIZES.MINI"
                />
              </UserSummaryPopover>
            </div>
            <Menu right :options="actionOptions(task)">
              <template v-slot:activator="{ on, value }">
                <div
                  class="ml-4 cursor-pointer d-flex justify-center options-icon"
                >
                  <v-icon
                    v-if="hover || value"
                    v-on="on"
                    color="blue-grey darken-1"
                    size="20"
                    >mdi-dots-vertical
                  </v-icon>
                </div>
              </template>
            </Menu>
          </div>
        </div>
      </v-hover>
    </div>
    <div class="text-center pt-1 mb-n3" v-if="shouldPaginate">
      <v-btn
        text
        plain
        color="blue"
        class="v-btn--active"
        :ripple="false"
        :loading="isLoading"
        @click="$emit('viewMore')"
      >
        {{ $t('common.button.view_more') }}
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'

import { IMAGE_SIZES } from 'rever-media-sdk'
import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import taskPermissions from '@/mixins/taskPermissions.vue'
import mixin from '@/mixins/mixin.vue'
import config from '@/config'

import PriorityIcon from '@/components/PriorityIcon.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import UserSummaryPopover from '@/components/UserSummaryPopover.vue'
import Menu from '@/components/UI/Menu.vue'

export default {
  components: { PriorityIcon, UserAvatar, Menu, UserSummaryPopover },
  props: {
    tasks: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isInitialLoading: {
      type: Boolean,
      default: false,
    },
    shouldPaginate: {
      type: Boolean,
      default: false,
    },
    groupKey: {
      type: String,
      default: () => '',
    },
    hideOnEmpty: {
      type: Boolean,
      default: () => false,
    },
  },

  mixins: [mixin, trackerMixin, taskPermissions],

  data() {
    return {
      IMAGE_SIZES,
    }
  },

  methods: {
    copyToClipboard(taskId) {
      navigator.clipboard.writeText(`${config.webAppURL}/tasks?task=${taskId}`)
      this.showInfo(this.$t('common.link_copy_to_clipboard'))
      this.trackTasks('tasks.copyLink', {
        taskId: taskId,
        from: 'taskView',
      })
    },
    actionOptions(task) {
      const options = [
        {
          id: 'copy-link',
          icon: 'mdi-link-variant',
          title: this.$t('common.copy_link'),
          action: () => this.copyToClipboard(task.id),
        },
      ]

      task.canEdit &&
        options.push({
          icon: 'mdi-content-copy',
          id: 'duplicate',
          title: this.$t('common.button.duplicate'),
          action: () => this.$emit('duplicate', task),
        })

      task.canDelete &&
        options.push({
          icon: 'mdi-trash-can-outline',
          id: 'delete',
          title: this.$t('common.button.delete'),
          action: () => this.$emit('delete', task.id),
        })

      return options
    },
  },

  computed: {
    parsedTasks() {
      return !_.isEmpty(this.tasks)
        ? this.tasks.map(task => {
            return {
              ...task,
              dueDateText: task?.dueDate
                ? utils.dates.humanDueDate(task.dueDate)
                : '',
              isOverdue: task?.dueDate
                ? moment(task.dueDate)
                    .endOf('day')
                    .isBefore(moment().endOf('day')) &&
                  task.status !== this.$constants.tasksStatus.completed
                : false,
              isCompleted:
                task.status === this.$constants.tasksStatus.completed,
              assignees: task.assignees || [],
              canEdit: this.canEditTask({ task }),
              canDelete: this.canDeleteTask({ task }),
            }
          })
        : []
    },
    hasTasks() {
      return !_.isEmpty(this.tasks)
    },
  },
}
</script>

<style lang="scss">
.task-relation-list {
  .divider-bottom {
    border-bottom: 1px solid $color-blue-grey-lighten2;
  }
  .v-expansion-panel-header {
    min-height: 0 !important;
  }
  .v-expansion-panel-content__wrap {
    padding: 0 !important;
  }
  .completed-item {
    opacity: 0.6;
  }
  .icon-bubble-avatar {
    width: 32px;
  }
  .options-icon {
    min-width: 20px;
  }
  .task-item:hover {
    background: $color-blue-grey-lighten3;
  }
  border-top: 1px solid $color-blue-grey-lighten2;
}
</style>
